import ReactEcharts from "echarts-for-react";
import React from "react";

import { SessionStore, LocaleStore, RouterStore } from "@/stores";
import { inject, observer } from "mobx-react";
import { powerUnit2object, powerUnit2jsonLTC, TimeFormatType, timestampFormat } from "@/utils";

interface StoreProps {
  session: SessionStore;
  router: RouterStore;
  locale: LocaleStore;
}

interface MyChartProps {
  data: any;
  type: "power" | "miner" | "earn" | "pool" | "block";
  istotal?: boolean;
  [x: string]: any;
}
interface Props extends StoreProps, MyChartProps {}
@inject("session", "locale", "router")
@observer
class MyChart extends React.Component<Props, any> {
  constructor(props) {
    super(props);
  }
  state = {} as any;

  componentDidMount() {}

  formatTime = (value, isTimeStamp?: boolean) => {
    let temp = "";
    if (isTimeStamp) {
      temp = timestampFormat(value, TimeFormatType.Minute).replace(/\s/g, "\n");
    } else {
      temp = value.replace(/\s/g, "\n");
    }
    return temp;
  };

  formatPowerTip = (params) => {
    let output = "<b>" + timestampFormat(params[0].name, TimeFormatType.Minute) + "</b><br/>";
    for (let i = 0; i < params.length; i++) {
      const json = powerUnit2object(+params[i].value);
      output += params[i].marker + params[i].seriesName + ": " + json.value + " " + json.unit;

      if (i != params.length - 1) {
        output += "<br/>";
      }
    }
    return output;
  };

  formatPowerTipLTC = (params) => {
    let output = "<b>" + timestampFormat(params[0].name, TimeFormatType.Minute) + "</b><br/>";
    for (let i = 0; i < params.length; i++) {
      const json = powerUnit2jsonLTC(+params[i].value);
      output += params[i].marker + params[i].seriesName + ": " + json.value + " " + json.unit;

      if (i != params.length - 1) {
        output += "<br/>";
      }
    }
    return output;
  };

  formatTimeTip = (params) => {
    let output = "<b>" + timestampFormat(params[0].name, TimeFormatType.Minute) + "</b><br/>";
    for (let i = 0; i < params.length; i++) {
      output += params[i].marker + params[i].seriesName + ":" + params[i].value;
      if (i != params.length - 1) {
        output += "<br/>";
      }
    }
    return output;
  };

  // 算力图表
  drawPower = () => {
    const { data, locale, istotal } = this.props;
    let option = {
      color: ["#1890FF"],
      tooltip: {
        trigger: "axis",
        formatter: (params) =>
          data.currency != "LTC" ? this.formatPowerTip(params) : this.formatPowerTipLTC(params),
      },
      grid: {
        top: "8%",
        left: "2%",
        right: "10%",
        bottom: "2%",
        width: 1080,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.x,
        splitNumber: 2,
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisLabel: {
          margin: 30,
          interval: 100000,
          formatter: (value) => this.formatTime(value, true),
          showMaxLabel: true,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        splitLine: {
          lineStyle: {
            color: "#E1E5FE",
            type: "dashed",
          },
        },
        axisLabel: {
          margin: 20,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
      },
      series: [
        {
          name: istotal ? locale.get("IndexPage.totalHashrates") : locale.get("power"),
          type: "line",
          stack: istotal ? locale.get("IndexPage.totalHashrates") : locale.get("power"),
          smooth: true,
          data: data.currency != "LTC" ? data.y_total : data.y_ltc,
          // areaStyle: {},
          lineStyle: {
            normal: {
              width: 3,
              shadowColor: "rgba(107, 125, 251, 0.3)",
              shadowBlur: 4,
              shadowOffsetY: 10,
            },
          },
        },
      ],
    };
    return option;
  };
  // 矿工图表
  drawMiner = () => {
    const { data, locale } = this.props;
    let option = {
      color: ["#00D1C3"],
      tooltip: {
        trigger: "axis",
        formatter: (params) => this.formatTimeTip(params),
      },
      grid: {
        top: "8%",
        left: "2%",
        right: "10%",
        bottom: "2%",
        width: 1080,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.x,
        splitNumber: 2,
        axisLabel: {
          margin: 30,
          interval: 100000,
          formatter: (value) => this.formatTime(value, true),
          showMinLabel: true,
          showMaxLabel: true,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisLabel: {
          margin: 20,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        splitLine: {
          lineStyle: {
            color: "#E1E5FE",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: locale.get("IndexPage.Miner.online"),
          type: "line",
          stack: locale.get("IndexPage.Miner.online"),
          smooth: true,
          data: data.y,
          // areaStyle: {},
          lineStyle: {
            normal: {
              width: 3,
              shadowColor: "rgba(48, 167, 75, 0.3)",
              shadowBlur: 4,
              shadowOffsetY: 10,
            },
          },
        },
      ],
    };
    return option;
  };
  // 收益图表
  drawIncome = () => {
    const { data, locale } = this.props;
    let option = {
      color: ["#F28544", "#F2BC44"],
      tooltip: {
        trigger: "axis",
        formatter: (params) => this.formatTimeTip(params),
      },
      grid: {
        top: "10%",
        left: "2%",
        right: "2%",
        bottom: "2%",
        width: 1080,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.x,
        splitNumber: 2,
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisLabel: {
          margin: 30,
          interval: 100000,
          formatter: (value) => this.formatTime(value, true),
          showMinLabel: true,
          showMaxLabel: true,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: [
        {
          name: data.currency !== "LTC" ? "BTC" : "LTC",
          type: "value",
          axisLine: {
            lineStyle: {
              color: "#878999",
              type: "solid",
            },
          },
          axisLabel: {
            margin: 20,
            textStyle: {
              color: "#000",
              fontSize: "14",
            },
          },
          splitLine: {
            lineStyle: {
              color: "#E1E5FE",
              type: "dashed",
            },
          },
        },
        {
          name: data.currency !== "LTC" ? "BSV" : "DOGE",
          type: "value",
          axisLine: {
            lineStyle: {
              color: "#878999",
              type: "solid",
            },
          },
          splitLine: {
            lineStyle: {
              color: "#E1E5FE",
              type: "dashed",
            },
          },
          axisLabel: {
            margin: 20,
            textStyle: {
              color: "#000",
              fontSize: "14",
            },
          },
        },
      ],
      series:
        data.currency !== "LTC"
          ? [
              {
                name: "BTC",
                type: "line",
                stack: "BTC",
                smooth: true,
                data: data.y_btc,
                lineStyle: {
                  normal: {
                    width: 3,
                    shadowColor: "rgba(107, 125, 251, 0.3)",
                    shadowBlur: 4,
                    shadowOffsetY: 10,
                  },
                },
                // areaStyle: {},
              },
              {
                name: "BSV",
                type: "line",
                stack: "BSV",
                smooth: true,
                data: data.y_bsv,
                yAxisIndex: 1,
                lineStyle: {
                  normal: {
                    width: 3,
                    shadowColor: "rgba(235, 71, 66, 0.2)",
                    shadowBlur: 4,
                    shadowOffsetY: 10,
                  },
                },
                // areaStyle: {},
              },
            ]
          : [
              {
                name: "LTC",
                type: "line",
                stack: "LTC",
                smooth: true,
                data: data.y_ltc,
                lineStyle: {
                  normal: {
                    width: 3,
                    shadowColor: "rgba(107, 125, 251, 0.3)",
                    shadowBlur: 4,
                    shadowOffsetY: 10,
                  },
                },
                // areaStyle: {},
              },
              {
                name: "DOGE",
                type: "line",
                stack: "DOGE",
                smooth: true,
                data: data.y_doge,
                yAxisIndex: 1,
                lineStyle: {
                  normal: {
                    width: 3,
                    shadowColor: "rgba(235, 71, 66, 0.2)",
                    shadowBlur: 4,
                    shadowOffsetY: 10,
                  },
                },
                // areaStyle: {},
              },
            ],
    };
    return option;
  };
  // 资金快照图表
  drawBalance = () => {
    const { data, locale } = this.props;
    let option = {
      color: ["#576DFF"],
      tooltip: {
        trigger: "axis",
      },
      grid: {
        top: "8%",
        left: "2%",
        right: "2%",
        bottom: "2%",
        width: 1100,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.x,
        splitNumber: 2,
        axisLabel: {
          margin: 30,
          interval: 100000,
          formatter: (value) => this.formatTime(value),
          showMaxLabel: true,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisLabel: {
          margin: 20,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        splitLine: {
          lineStyle: {
            color: "#E1E5FE",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: locale.get("IndexPage.Balance"),
          type: "line",
          stack: "BSV",
          smooth: true,
          data: data.y,
          lineStyle: {
            normal: {
              width: 3,
              shadowColor: "rgba(107, 125, 251, 0.3)",
              shadowBlur: 4,
              shadowOffsetY: 10,
            },
          },
          // areaStyle: {},
        },
      ],
    };
    return option;
  };
  // 出块统计图表
  drawBlock = () => {
    const { data, locale } = this.props;
    let option = {
      color: ["#576DFF"],
      tooltip: {
        trigger: "axis",
      },
      grid: {
        top: "8%",
        left: "2%",
        right: "2%",
        bottom: "2%",
        width: 1100,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.x,
        splitNumber: 2,
        axisLabel: {
          margin: 30,
          interval: 100000,
          formatter: (value) => this.formatTime(value),
          showMaxLabel: true,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: "#E1E5FE",
          },
        },
        axisLabel: {
          margin: 20,
          textStyle: {
            color: "#000",
            fontSize: "14",
          },
        },
        splitLine: {
          lineStyle: {
            color: "#E1E5FE",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: locale.get("treasury.BSVBlocks"),
          type: "line",
          stack: "BSV",
          smooth: true,
          data: data.y,
          lineStyle: {
            normal: {
              width: 3,
              shadowColor: "rgba(107, 125, 251, 0.3)",
              shadowBlur: 4,
              shadowOffsetY: 10,
            },
          },
          // areaStyle: {},
        },
      ],
    };
    return option;
  };

  render() {
    const { type, data } = this.props;
    if (!data) return null;
    let current = {};
    if (type == "power") {
      current = this.drawPower();
    } else if (type == "miner") {
      current = this.drawMiner();
    } else if (type == "earn") {
      current = this.drawIncome();
    } else if (type == "pool") {
      current = this.drawBalance();
    } else {
      current = this.drawBlock();
    }
    return <ReactEcharts className="charts" option={current} notMerge={true} lazyUpdate={true} />;
  }
}

export default MyChart as React.ComponentType<MyChartProps>;
